<template>
  <res-dialog :showDialog="showDialog"
              title="Export Budget"
              :titleClass="'resBlue darken-1 white--text'"
              :iconColor="'white'"
              :maxWidth="maxWidth"
              :setWidth="width"
              saveButtonText="EXPORT"
              cancelButtonText="CANCEL"
              :setHeight="height"
              @cancel="cancel"
              @save="save">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                @cancel="closeHelp">
      <export-working-draft-help :openSection="openHelpSection"></export-working-draft-help>
    </res-dialog>
    <v-container>
      <v-row>
        <v-col cols="10" sm="10">
          <span class="font-weight-medium">Select your Budget export options, then select 'Export'.</span>
        </v-col>
        <v-col cols="2" sm="2">
          <div class="help-controls" @click="openHelp(0)">
            <span class="help-label">Help</span>
            <v-icon color="var(--v-resBlue-darken1)" class="pb-1" small>mdi-help-circle</v-icon>
          </div>
        </v-col>
      </v-row>
      <div class="dialog-radio-controls">
        <res-radio-group label="Select export format:"
                         labelColor="gray"
                         :value="budgetToExport.selectedExportFormat"
                         :options="exportFormatRadioOptions"
                         @input="selectedExportFormatChanged" class="d-flex justify-space-between">
        </res-radio-group>
      </div>
      <div v-if="displayExpandedExportOptions">
        <h2 class="res-line-around-subheader">WORKING DRAFT</h2>

        <div class="dialog-radio-controls">
          <res-radio-group label="Include Calculations Column?"
                           labelColor="gray"
                           :value="budgetToExport.includeCalculations"
                           :options="includeCalculationsRadioOptions"
                           @input="includeCalculationsChanged" class="d-flex justify-space-between">
          </res-radio-group>
          <v-divider></v-divider>
          <res-radio-group label="Include Assumptions Column?"
                           labelColor="gray"
                           :value="budgetToExport.includeAssumptions"
                           :options="includeAssumptionsRadioOptions"
                           @input="includeAssumptionsChanged" class="d-flex justify-space-between">
          </res-radio-group>
          <v-divider></v-divider>
          <res-radio-group label="Include Per Unit Column?"
                           labelColor="gray"
                           :value="budgetToExport.includePerUnit"
                           :options="includeColumnOptions"
                           @input="includeUnitColumnsChanged" class="d-flex justify-space-between">
          </res-radio-group>
          <v-divider></v-divider>
          <res-radio-group label="Include Per Square Foot Column?"
                           labelColor="gray"
                           :value="budgetToExport.includePerSqft"
                           :options="includeColumnOptions"
                           @input="includeSqftColumnsChanged" class="d-flex justify-space-between">
          </res-radio-group>
          <v-divider></v-divider>
          <res-radio-group label="Include Other (Balance Sheet) Accounts?"
                           labelColor="gray"
                           :value="budgetToExport.includeBalanceSheetAccounts"
                           :options="includeBalanceSheetAccountsRadioOptions"
                           @input="includeBalanceSheetAccountsChanged" class="d-flex justify-space-between">
          </res-radio-group>
          <v-divider></v-divider>
          <res-radio-group label="Hide Zero Only Rows?"
                           labelColor="gray"
                           :value="budgetToExport.zeroRowsDisplayed"
                           :options="zeroRowsDisplayedRadioOptions"
                           @input="zeroRowsDisplayedChanged" class="d-flex justify-space-between">
          </res-radio-group>
          <v-divider></v-divider>
          <res-radio-group label="Display Working Rows?"
                           labelColor="gray"
                           :value="budgetToExport.workingRowsDisplayed"
                           :options="workingRowsDisplayedRadioOptions"
                           @input="workingRowsDisplayedChanged" class="d-flex justify-space-between">
          </res-radio-group>
        </div>

        <h2 class="res-line-around-subheader">ADDITIONAL OPTIONS</h2>

        <div class="dialog-radio-controls">
          <res-radio-group label="Include Market rent + Occupancy Worksheets?"
                           labelColor="gray"
                           :value="budgetToExport.includeWorksheets"
                           :options="includeWorksheetsRadioOptions"
                           @input="includeWorksheetsChanged" class="d-flex justify-space-between">
          </res-radio-group>
          <v-divider></v-divider>
          <res-radio-group label="Include Settings?"
                           labelColor="gray"
                           :value="budgetToExport.includeSettings"
                           :options="includeSettingsRadioOptions"
                           @input="includeSettingsChanged" class="d-flex justify-space-between">
          </res-radio-group>
        </div>
      </div>
      <div v-else>
        <v-row><v-col cols="10" sm="10">Select 'Export' button to export in import-ready CSV format.</v-col></v-row>
      </div>
    </v-container>
  </res-dialog>
</template>

<script>
  import ResDialog from '@/components/base/ResDialog.vue';
  import ResRadioGroup from "@/components/base/ResRadioGroup";

  import HelpableMixin from '@/mixins/Helpable.js';
  import ExportWorkingDraftHelp from '@/components/help/ExportWorkingDraftHelp.vue';

  export default {    
    props: {
      height: {
        type: Number
      },
      maxWidth: {
        type: Number
      },
      showDialog: {
        type: Boolean
      },
      width: {
        type: Number
      },
      budgetIterationId: {
        type: String
      },
    },
    mixins: [HelpableMixin],
    data() {
      return {
        budgetToExport: {
          selectedExportFormat: 1,
          includeCalculations: false, // default to No
          includeAssumptions: true,// default to Yes
          includePerUnit: true,
          includePerSqft: false,
          includeBalanceSheetAccounts: false, // default to No
          zeroRowsDisplayed: false, // default to Yes = Hide
          workingRowsDisplayed: true, // default to Yes
          includeWorksheets: false, // default to No
          includeSettings: false, // default to No
          budgetIterationId: null,
        },
        exportFormatRadioOptions: [
          { label: 'EXCEL FORMAT', value: 1 },
          { label: 'CSV FORMAT', value: 0 }
        ],
        includeCalculationsRadioOptions: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ],
        includeAssumptionsRadioOptions: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ],
        includeColumnOptions: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ],
        includeBalanceSheetAccountsRadioOptions: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ],
        zeroRowsDisplayedRadioOptions: [
          { label: 'Yes', value: false },
          { label: 'No', value: true }
        ],
        workingRowsDisplayedRadioOptions: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ],
        includeWorksheetsRadioOptions: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ],
        includeSettingsRadioOptions: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ],
      }
    },
    components: {    
      ResDialog,
      ResRadioGroup,
      ExportWorkingDraftHelp
    },
    methods: {
      selectedExportFormatChanged(selectedExportFormat) {
        this.budgetToExport.selectedExportFormat = selectedExportFormat;
      },
      includeCalculationsChanged(includeCalculations) {
        this.budgetToExport.includeCalculations = includeCalculations;
      },
      includeAssumptionsChanged(includeAssumptions) {
        this.budgetToExport.includeAssumptions = includeAssumptions;
      },
      includeUnitColumnsChanged(includeColumn) {
        this.budgetToExport.includePerUnit = includeColumn;
      },
      includeSqftColumnsChanged(includeColumn) {
        this.budgetToExport.includePerSqft = includeColumn;
      },
      includeBalanceSheetAccountsChanged(includeBalanceSheetAccounts) {
        this.budgetToExport.includeBalanceSheetAccounts = includeBalanceSheetAccounts;
      },
      zeroRowsDisplayedChanged(displayZeroRows) {
        this.budgetToExport.zeroRowsDisplayed = displayZeroRows;
      },
      workingRowsDisplayedChanged(displayWorkingRows) {
        this.budgetToExport.workingRowsDisplayed = displayWorkingRows;
      },
      includeWorksheetsChanged(includeWorksheets) {
        this.budgetToExport.includeWorksheets = includeWorksheets;
      },
      includeSettingsChanged(includeSettings) {
        this.budgetToExport.includeSettings = includeSettings;
      },
      save () {     
        this.$emit('save', this.budgetToExport)
      },
      cancel () {
        this.$emit('cancel', true)
      },
    },
    computed: {
      displayExpandedExportOptions() {
        return this.budgetToExport.selectedExportFormat == 1;
      },
    },
    async mounted() {
      this.budgetToExport.budgetIterationId = this.budgetIterationId;
    },
  };
</script>

<style lang="scss" scoped>
  .base-dialog {
    .res-col-10 {
      padding-top: 10px !important;
      padding-bottom: 10px !important;
    }
    .v-btn-toggle--rounded {
      border-radius: 6px;
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
      border-bottom-left-radius: 6px;
    }
  }
  .theme--light.v-btn-toggle::v-deep {
    .v-btn.v-btn {
      border: solid 1px var(--v-resGrey-darken1) !important;
    }
  }
  h2.res-line-around-subheader {
    width: 100%;
    margin: .7em auto;
    overflow: hidden;
    text-align: center;
    font-weight: 500;
    font-size: initial !important;
    color: var(--v-resGrey-base);
  }

  h2.res-line-around-subheader:before, h2.res-line-around-subheader:after {
    content: "";
    display: inline-block;
    width: 50%;
    margin: 0 .5em 0 -55%;
    vertical-align: middle;
    border-bottom: 1px solid;
  }

  h2.res-line-around-subheader:after {
    margin: 0 -55% 0 .5em;
  }

  .grid-controls {
    width: 100%;
    height: 40px;
    margin-bottom: 14px;
    margin-top: -8px;
  }

  .dialog-radio-controls {
    display: inline-block;
    border: solid 1px var(--v-resGrey-lighten3);
    //height: 105px;
    width: 100%;

    div {
      padding: 6px 0 6px 10px;
    }
  }

  .add-columns-controls {
    display: inline-block;
    border: solid 1px var(--v-resGrey-lighten3);
    vertical-align: top;
    height: 100%;
    float: right !important;
  }

  .add-columns-label {
    vertical-align: top;
    display: inline-block;
    padding: 7px 8px 0 10px;
    height: 100%;
  }

  .add-columns-option::v-deep {
    display: inline-block;

    .v-label {
      font-size: 13px !important;
      color: var(--v-resGrey-darken2);
      font-weight: bold;
      padding-right: 14px;
      padding-top: 8px;
    }

    .v-input--selection-controls__input {
      margin-top: 0;
      padding-top: 8px;
    }
    }

    .v-input.v-input--dense.v-input--selection-controls.v-input--radio-group.v-input--radio-group--row::v-deep {
      .v-input--selection-controls__input {
        height: 20px !important;
        width: 20px !important;
      }

      .v-input--selection-controls__ripple {
        width: 20px;
        height: 20px;
        left: -10px;
      }

      .v-icon--svg {
        height: 15px !important;
        width: 15px !important;
      }
    }


    .v-input#import-property-selector::v-deep {
      div.menuable__content__active {
        max-height: 118px !important;
      }
    }

    .v-input#import-start-month-selector::v-deep {
      div.menuable__content__active {
        max-height: 118px !important;
      }
    }

    .v-input#import-year-selector::v-deep {
      div.menuable__content__active {
        max-height: 118px !important;
      }
    }



    .help-controls {
      height: 100%;
      float: right;
      border: solid 1px var(--v-resGrey-lighten3);
      padding: 7px 6px 6px 6px;
    }

    .help-label {
      padding-right: 6px;
    }

    .v-alert::v-deep {
      .v-alert__wrapper {
        color: #4f4f4f;
        font-size: 12px;

        .v-icon {
          color: #4f4f4f;
        }
      }
    }
</style>
